import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { equityDashboardAPI } from '../../services/equityDashboardApi';

const ZoneCard: React.FC<{ zone: any; color: string }> = ({ zone, color }) => (
  <Paper sx={{ p: 3, borderTop: `4px solid ${color}`, height: '100%' }}>
    <Typography variant="h5" fontWeight={700} color={color} mb={2}>{zone.zone}</Typography>
    <Grid container spacing={1}>
      {[
        { label: 'Students', value: zone.total_students?.toLocaleString() },
        { label: 'Teachers', value: zone.total_teachers?.toLocaleString() },
        { label: 'Schools', value: zone.total_schools?.toLocaleString() },
        { label: 'PTR', value: `${zone.ptr}:1` },
        { label: 'GPI', value: zone.gpi?.toFixed(3) },
        { label: 'Completion Rate', value: `${zone.primary_completion_rate}%` },
        { label: 'Female Students', value: zone.female_students?.toLocaleString() },
        { label: 'Male Students', value: zone.male_students?.toLocaleString() },
      ].map((item) => (
        <Grid item xs={6} key={item.label}>
          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          <Typography variant="body1" fontWeight={600}>{item.value}</Typography>
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const getEquityColor = (score: number) => {
  if (score >= 70) return '#4caf50';
  if (score >= 50) return '#ff9800';
  return '#f44336';
};

const EquityDashboard: React.FC = () => {
  const { data: zoneData, isLoading: loadingZones } = useQuery({
    queryKey: ['equity-zone-comparison'],
    queryFn: () => equityDashboardAPI.getZoneComparison(),
    select: (res) => res.data,
  });

  const { data: districtData, isLoading: loadingDistricts } = useQuery({
    queryKey: ['equity-district-details'],
    queryFn: () => equityDashboardAPI.getDistrictDetails(),
    select: (res) => res.data,
  });

  if (loadingZones || loadingDistricts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const unguja = zoneData?.zones?.['Unguja'];
  const pemba = zoneData?.zones?.['Pemba'];

  // Radar chart data
  const radarData = unguja?.dimensions
    ? Object.keys(unguja.dimensions).map((key) => ({
        dimension: key,
        Unguja: unguja.dimensions[key],
        Pemba: pemba?.dimensions?.[key] || 0,
      }))
    : [];

  // Equity score bar chart
  const equityBarData = districtData?.districts?.map((d: any) => ({
    district: d.district,
    equity_score: d.equity_score,
    zone: d.zone,
  })) || [];

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight={700} mb={0.5}>
        District Equity Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Unguja vs Pemba multi-dimensional equity comparison
        {zoneData?.academic_year && ` (${zoneData.academic_year})`}
      </Typography>

      {/* Equity Gaps Summary */}
      {zoneData?.comparison_summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <Typography variant="h4" fontWeight={700} color="#e65100">
                {zoneData.comparison_summary.ptr_gap?.toFixed(1)}
              </Typography>
              <Typography variant="body2">PTR Gap (Unguja vs Pemba)</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="h4" fontWeight={700} color="#1565c0">
                {zoneData.comparison_summary.gpi_gap?.toFixed(3)}
              </Typography>
              <Typography variant="body2">GPI Gap</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }}>
              <Typography variant="h4" fontWeight={700} color="#c62828">
                {zoneData.comparison_summary.completion_gap?.toFixed(1)}%
              </Typography>
              <Typography variant="body2">Completion Rate Gap</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Zone Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          {unguja && <ZoneCard zone={unguja} color="#1976d2" />}
        </Grid>
        <Grid item xs={12} md={6}>
          {pemba && <ZoneCard zone={pemba} color="#ff9800" />}
        </Grid>
      </Grid>

      {/* Radar Chart */}
      {radarData.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Multi-Dimensional Comparison</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} />
              <Radar name="Unguja" dataKey="Unguja" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
              <Radar name="Pemba" dataKey="Pemba" stroke="#ff9800" fill="#ff9800" fillOpacity={0.3} />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* District Equity Scores */}
      {equityBarData.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>District Equity Scores</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={equityBarData} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis type="category" dataKey="district" width={110} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="equity_score" name="Equity Score" radius={[0, 4, 4, 0]}>
                {equityBarData.map((entry: any, index: number) => (
                  <Cell key={index} fill={getEquityColor(entry.equity_score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* District Details Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>District-Level Details</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>District</strong></TableCell>
                <TableCell><strong>Zone</strong></TableCell>
                <TableCell align="right"><strong>Students</strong></TableCell>
                <TableCell align="right"><strong>PTR</strong></TableCell>
                <TableCell align="right"><strong>GPI</strong></TableCell>
                <TableCell align="right"><strong>Completion</strong></TableCell>
                <TableCell align="right"><strong>Equity Score</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {districtData?.districts?.map((d: any) => (
                <TableRow key={d.district}>
                  <TableCell>{d.district}</TableCell>
                  <TableCell>
                    <Chip label={d.zone} size="small" color={d.zone === 'Pemba' ? 'warning' : 'info'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">{d.total_students?.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip label={`${d.ptr}:1`} size="small" sx={{ bgcolor: d.ptr > 40 ? '#f44336' : '#4caf50', color: 'white', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{d.gpi?.toFixed(3)}</TableCell>
                  <TableCell align="right">{d.primary_completion_rate}%</TableCell>
                  <TableCell align="right">
                    <Chip label={d.equity_score} size="small" sx={{ bgcolor: getEquityColor(d.equity_score), color: 'white', fontWeight: 600 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EquityDashboard;
